import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { HoldResponseDto, HoldStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { HoldsIcon, TomTableEmptyCell, useMinimalsTheme } from '@tom-ui/ui'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'

type HoldWithStatus = Pick<HoldResponseDto, 'status' | 'holdType' | 'reason'>

interface Props {
  holds: HoldWithStatus[]
  onClick?: () => Promise<void> | void
  hideText?: boolean
}

const chipSx = (bg: string, clickable?: boolean) => ({
  cursor: clickable ? 'pointer' : 'default',
  background: bg,
  padding: '4px 8px',
  borderRadius: '4px',
  display: 'flex',
  height: '32px',
  width: 'fit-content',
})

export const HoldsChip: FC<Props> = observer(({ holds, onClick, hideText }) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const activeHolds = holds.filter(h => h.status === HoldStatus.Active)
  const activeHoldsCount = activeHolds.length

  const chipColor = activeHoldsCount ? theme.palette.warning.dark : theme.palette.grey[600]
  const chipBgColor = activeHoldsCount ? theme.palette.warning.lighter : theme.palette.grey[200]

  const tooltipContent =
    activeHolds.length > 0 ? (
      <>
        {activeHolds.map((h, i) => {
          const name = h.holdType?.name
          const reason = h.reason
          const line = name && reason ? `${name}: ${reason}` : (name ?? reason ?? '')
          return line ? <div key={i}>{line}</div> : null
        })}
      </>
    ) : undefined

  return (
    <Tooltip title={tooltipContent} enterDelay={500} disableHoverListener={!tooltipContent}>
      <Box onClick={onClick} sx={chipSx(chipBgColor, !!onClick)}>
        <Typography
          variant='captionBold'
          display={'flex'}
          alignSelf={'center'}
          alignItems={'center'}
          color={chipColor}
        >
          {hideText === true
            ? activeHoldsCount
            : `${activeHoldsCount} ${activeHoldsCount > 1 ? `${t('holds', 'holds')}` : `${t('hold', 'hold')}`}`}
          <HoldsIcon sx={{ marginLeft: '4px' }} />
        </Typography>
      </Box>
    </Tooltip>
  )
})

export const HoldTypeChips: FC<Props> = observer(({ holds, onClick }) => {
  const theme = useMinimalsTheme()

  const activeTypes = useMemo(
    () =>
      _.uniqBy(
        holds.filter(h => h.status === HoldStatus.Active && h.holdType?.name),
        h => h.holdType!.name,
      ).map(h => h.holdType!.name!),
    [holds],
  )

  return (
    <Box display='flex' gap={1} flexWrap='wrap'>
      {activeTypes.map(name => (
        <Box key={name} onClick={onClick} sx={chipSx(theme.palette.warning.lighter, !!onClick)}>
          <Typography
            variant='captionBold'
            display={'flex'}
            alignSelf={'center'}
            alignItems={'center'}
            color={theme.palette.warning.dark}
          >
            {name}
            <HoldsIcon sx={{ marginLeft: '4px' }} />
          </Typography>
        </Box>
      ))}
    </Box>
  )
})

export const HoldTypeNameChips: FC<{ names: string[] }> = observer(({ names }) => {
  const theme = useMinimalsTheme()

  return (
    <Box display='flex' gap={1} flexWrap='wrap'>
      {names.map(name => (
        <Box key={name} sx={chipSx(theme.palette.warning.lighter)}>
          <Typography
            variant='captionBold'
            display='flex'
            alignSelf='center'
            alignItems='center'
            color={theme.palette.warning.dark}
          >
            {name}
            <HoldsIcon sx={{ marginLeft: '4px' }} />
          </Typography>
        </Box>
      ))}
    </Box>
  )
})

export const HoldTypeChipsCell = observer(
  ({ containerJourneyId }: { containerJourneyId?: number | null }) => {
    const { holdItemStore } = usePlanningStore()
    const holds =
      containerJourneyId != null
        ? (holdItemStore.holdsByContainerJourneyId[containerJourneyId] ?? [])
        : []
    return holds.length > 0 ? <HoldTypeChips holds={holds} /> : <TomTableEmptyCell />
  },
)

export const HoldsIconByJourneyId = observer(
  ({ containerJourneyId }: { containerJourneyId?: number | null }) => {
    const { holdItemStore } = usePlanningStore()
    const theme = useMinimalsTheme()
    const { t } = useTranslate()

    const holds =
      containerJourneyId != null
        ? (holdItemStore.holdsByContainerJourneyId[containerJourneyId] ?? [])
        : []
    const activeHolds = holds.filter(h => h.status === HoldStatus.Active)
    if (activeHolds.length === 0) return null

    const typeNames = _.uniq(activeHolds.map(h => h.holdType?.name).filter((n): n is string => !!n))
    const title = t('containerHasNActiveHolds', 'Container has {count} active holds: {names}', {
      count: activeHolds.length,
      names: typeNames.join(', '),
    })

    return (
      <Tooltip title={title} enterDelay={500}>
        <Stack color={theme.palette.text.primary}>
          <HoldsIcon />
        </Stack>
      </Tooltip>
    )
  },
)

export const HoldsChipByJourneyId = observer(
  ({
    containerJourneyId,
    onClick,
    hideText,
  }: {
    containerJourneyId?: number | null
    onClick?: () => Promise<void> | void
    hideText?: boolean
  }) => {
    const { holdItemStore } = usePlanningStore()
    const holds =
      containerJourneyId != null
        ? (holdItemStore.holdsByContainerJourneyId[containerJourneyId] ?? [])
        : []
    return <HoldsChip holds={holds} onClick={onClick} hideText={hideText} />
  },
)
